import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { Trip, TripLeg } from '@shared/types';
import { useAuth } from '@/app/AuthContext';
import { ApiError, api } from '@/lib/api';
import {
  legCardClass,
  paymentLabel,
  routeLine,
  shortCarrierName,
  statusBadgeClass,
  statusLabel,
} from '@/lib/travelCard';

interface TripsResponse {
  trips: Trip[];
}

type TransferDraft = {
  travelers: string;
  carrier_or_company: string;
  when_label: string;
  depart_at: string;
  origin: string;
  destination: string;
  pickup_address: string;
  dropoff_address: string;
  confirmation: string;
  price: string;
  status: 'booked' | 'needed' | 'cancel_requested';
  notes: string;
};

const EMPTY_DRAFT: TransferDraft = {
  travelers: '',
  carrier_or_company: '',
  when_label: '',
  depart_at: '',
  origin: '',
  destination: '',
  pickup_address: '',
  dropoff_address: '',
  confirmation: '',
  price: '',
  status: 'booked',
  notes: '',
};

const RETURN_RIDES_NOTE = "Lisa's return rides are listed under Nov 16–23.";

function neededCount(trip: Trip): number {
  const seen = new Set<string>();
  let count = 0;
  for (const leg of trip.legs) {
    if (leg.kind !== 'transfer' || leg.status !== 'needed') continue;
    const key = leg.link_key ?? `leg:${leg.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    count += 1;
  }
  return count;
}

function notesBody(notes: string | null): string | null {
  if (!notes) return null;
  const body = notes.replace(RETURN_RIDES_NOTE, '').replace(/\s{2,}/g, ' ').trim();
  return body.length > 0 ? body : null;
}

function draftFromLeg(leg: TripLeg): TransferDraft {
  return {
    travelers: leg.travelers ?? '',
    carrier_or_company: leg.carrier_or_company ?? '',
    when_label: leg.when_label ?? '',
    depart_at: leg.depart_at ?? '',
    origin: leg.origin ?? '',
    destination: leg.destination ?? '',
    pickup_address: leg.pickup_address ?? '',
    dropoff_address: leg.dropoff_address ?? '',
    confirmation: leg.confirmation ?? '',
    price: leg.price ?? '',
    status: leg.status === 'needed' || leg.status === 'cancel_requested' ? leg.status : 'booked',
    notes: leg.notes ?? '',
  };
}

function editStatus(value: string): TransferDraft['status'] {
  if (value === 'needed' || value === 'cancel_requested') return value;
  return 'booked';
}

function WriteUnlock({ onUnlocked }: { onUnlocked: () => void }) {
  const { login } = useAuth();
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await login(pin);
      setPin('');
      onUnlocked();
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError('Too many attempts. Please wait a few minutes and try again.');
      } else if (err instanceof ApiError) {
        setError(err.message || 'Incorrect PIN.');
      } else {
        setError('Could not unlock writes.');
      }
      setPin('');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="card">
      <h2>Unlock transfer edits</h2>
      <p style={{ fontSize: 14, color: 'var(--color-ink-muted)', lineHeight: 1.5 }}>
        Browsing stays public. Saving a transfer needs the family PIN.
      </p>
      <div className="field" style={{ marginTop: 12 }}>
        <label htmlFor="travel-pin">Family PIN</label>
        <input
          id="travel-pin"
          type="password"
          inputMode="numeric"
          autoComplete="off"
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
          maxLength={10}
          required
        />
      </div>
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
      <button type="submit" className="btn btn-primary btn-block" disabled={submitting || pin.length === 0}>
        {submitting ? 'Checking…' : 'Unlock'}
      </button>
    </form>
  );
}

function TransferForm({
  idPrefix,
  draft,
  onChange,
  onSubmit,
  onCancel,
  submitting,
  error,
  submitLabel,
}: {
  idPrefix: string;
  draft: TransferDraft;
  onChange: (next: TransferDraft) => void;
  onSubmit: (e: FormEvent) => void;
  onCancel: () => void;
  submitting: boolean;
  error: string | null;
  submitLabel: string;
}) {
  function set<K extends keyof TransferDraft>(key: K, value: TransferDraft[K]) {
    onChange({ ...draft, [key]: value });
  }

  const fieldId = (name: string) => `${idPrefix}-${name}`;

  return (
    <form onSubmit={onSubmit} style={{ marginTop: 10 }}>
      <div className="field">
        <label htmlFor={fieldId('who')}>Who</label>
        <input id={fieldId('who')} type="text" value={draft.travelers} onChange={(e) => set('travelers', e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor={fieldId('company')}>Company</label>
        <input id={fieldId('company')} type="text" value={draft.carrier_or_company} onChange={(e) => set('carrier_or_company', e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor={fieldId('when')}>When</label>
        <input id={fieldId('when')} type="text" value={draft.when_label} onChange={(e) => set('when_label', e.target.value)} required />
      </div>
      <div className="field">
        <label htmlFor={fieldId('from')}>From</label>
        <input id={fieldId('from')} type="text" value={draft.origin} onChange={(e) => set('origin', e.target.value)} required />
      </div>
      <div className="field">
        <label htmlFor={fieldId('to')}>To</label>
        <input id={fieldId('to')} type="text" value={draft.destination} onChange={(e) => set('destination', e.target.value)} required />
      </div>
      <div className="field">
        <label htmlFor={fieldId('pickup')}>Pickup address</label>
        <input id={fieldId('pickup')} type="text" value={draft.pickup_address} onChange={(e) => set('pickup_address', e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor={fieldId('dropoff')}>Dropoff address</label>
        <input id={fieldId('dropoff')} type="text" value={draft.dropoff_address} onChange={(e) => set('dropoff_address', e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor={fieldId('confirmation')}>Confirmation</label>
        <input id={fieldId('confirmation')} type="text" value={draft.confirmation} onChange={(e) => set('confirmation', e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor={fieldId('price')}>Price</label>
        <input id={fieldId('price')} type="text" value={draft.price} onChange={(e) => set('price', e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor={fieldId('status')}>Status</label>
        <select id={fieldId('status')} value={draft.status} onChange={(e) => set('status', editStatus(e.target.value))}>
          <option value="needed">NEEDED</option>
          <option value="booked">Booked</option>
          <option value="cancel_requested">Cancel requested</option>
        </select>
      </div>
      <div className="field">
        <label htmlFor={fieldId('notes')}>Notes</label>
        <textarea id={fieldId('notes')} value={draft.notes} onChange={(e) => set('notes', e.target.value)} />
      </div>
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Saving…' : submitLabel}
        </button>
        <button type="button" className="btn" onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function TripNotes({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="trip-notes">
      <button
        type="button"
        className="trip-notes__toggle"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span>Trip notes</span>
        <span aria-hidden="true">{open ? '▾' : '▸'}</span>
      </button>
      {open && <p className="trip-notes__body">{text}</p>}
    </div>
  );
}

function LegCard({
  leg,
  unlocked,
  onSaved,
}: {
  leg: TripLeg;
  unlocked: boolean;
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<TransferDraft>(() => draftFromLeg(leg));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const detailsId = `leg-${leg.id}-details`;
  const carrier = shortCarrierName(leg.carrier_or_company, leg.kind);
  const fullCarrier = leg.carrier_or_company && leg.carrier_or_company.trim() !== carrier ? leg.carrier_or_company : null;
  const payment = paymentLabel(leg.notes);

  async function save(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.put(`/admin/trip-legs/${leg.id}`, {
        travelers: draft.travelers,
        carrier_or_company: draft.carrier_or_company,
        when_label: draft.when_label,
        depart_at: draft.depart_at,
        origin: draft.origin,
        destination: draft.destination,
        pickup_address: draft.pickup_address,
        dropoff_address: draft.dropoff_address,
        confirmation: draft.confirmation,
        price: draft.price,
        status: draft.status,
        notes: draft.notes,
      });
      setEditing(false);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save this transfer.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <article className={legCardClass(leg.kind, leg.status)}>
      <button
        type="button"
        className="trip-leg__summary"
        aria-expanded={open}
        aria-controls={detailsId}
        onClick={() => {
          setOpen((value) => !value);
          setEditing(false);
        }}
      >
        <span className="trip-leg__topline">
          <strong className="trip-leg__who">{leg.travelers ?? 'Travelers not set'}</strong>
          <span className="trip-leg__aside">
            <span className={statusBadgeClass(leg.status)}>{statusLabel(leg.status)}</span>
            <span aria-hidden="true">{open ? '▾' : '▸'}</span>
          </span>
        </span>
        <span className="trip-leg__carrier">{carrier}</span>
        <span className="trip-leg__route">{routeLine(leg.origin, leg.destination)}</span>
        {leg.when_label && <span className="trip-leg__meta">{leg.when_label}</span>}
        <span className={leg.confirmation ? 'trip-leg__confirmation' : 'trip-leg__confirmation trip-leg__confirmation--empty'}>
          {leg.confirmation ?? 'No confirmation'}
        </span>
      </button>
      {open && (
        <div id={detailsId} className="trip-leg__details">
          {leg.kind === 'flight' && leg.ident && <div className="trip-leg__meta">{leg.ident}</div>}
          {fullCarrier && <div className="trip-leg__meta">{fullCarrier}</div>}
          {leg.pickup_address && (
            <div className="trip-leg__meta">
              <span className="trip-leg__label">Pickup</span> {leg.pickup_address}
            </div>
          )}
          {leg.dropoff_address && (
            <div className="trip-leg__meta">
              <span className="trip-leg__label">Dropoff</span> {leg.dropoff_address}
            </div>
          )}
          {leg.price && (
            <div className="trip-leg__meta">
              <span className="trip-leg__label">Price</span> {leg.price}
            </div>
          )}
          {payment && (
            <div className="trip-leg__meta">
              <span className="trip-leg__label">Payment</span> {payment}
            </div>
          )}
          {leg.ticket_number && <div className="trip-leg__meta">Ticket {leg.ticket_number}</div>}
          {leg.kind === 'flight' && (
            <div className="trip-leg__meta">{leg.seat ? `Seat ${leg.seat}` : 'Seat not picked yet'}</div>
          )}
          {leg.notes && <div className="trip-leg__meta">{leg.notes}</div>}
          {unlocked && leg.kind === 'transfer' && !editing && (
            <button
              type="button"
              className="btn"
              style={{ marginTop: 8 }}
              onClick={() => {
                setDraft(draftFromLeg(leg));
                setError(null);
                setEditing(true);
              }}
            >
              Edit
            </button>
          )}
          {editing && (
            <TransferForm
              idPrefix={`leg-${leg.id}`}
              draft={draft}
              onChange={setDraft}
              onSubmit={save}
              onCancel={() => setEditing(false)}
              submitting={submitting}
              error={error}
              submitLabel="Save transfer"
            />
          )}
        </div>
      )}
    </article>
  );
}

function AddTransfer({ slug, onSaved }: { slug: string; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<TransferDraft>(EMPTY_DRAFT);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.post(`/admin/trips/${slug}/legs`, {
        kind: 'transfer',
        travelers: draft.travelers,
        carrier_or_company: draft.carrier_or_company,
        when_label: draft.when_label,
        depart_at: draft.depart_at,
        origin: draft.origin,
        destination: draft.destination,
        pickup_address: draft.pickup_address,
        dropoff_address: draft.dropoff_address,
        confirmation: draft.confirmation,
        price: draft.price,
        status: draft.status,
        notes: draft.notes,
      });
      setDraft(EMPTY_DRAFT);
      setOpen(false);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add this transfer.');
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className="btn" onClick={() => setOpen(true)}>
        Add transfer
      </button>
    );
  }

  return (
    <div className="trip-leg">
      <strong>New transfer</strong>
      <TransferForm
        idPrefix={`add-${slug}`}
        draft={draft}
        onChange={setDraft}
        onSubmit={save}
        onCancel={() => setOpen(false)}
        submitting={submitting}
        error={error}
        submitLabel="Add transfer"
      />
    </div>
  );
}

export function TravelDetailsPage() {
  const [trips, setTrips] = useState<Trip[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openIds, setOpenIds] = useState<number[]>([]);
  const [unlocked, setUnlocked] = useState(false);

  const load = useCallback(() => {
    api
      .get<TripsResponse>('/trips')
      .then((res) => {
        setTrips(res.trips);
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Could not load travel details.');
      });
  }, []);

  useEffect(() => {
    load();
    api
      .get<{ authenticated: boolean }>('/auth/session')
      .then((res) => setUnlocked(res.authenticated))
      .catch(() => setUnlocked(false));
  }, [load]);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Travel details</h1>
        <p className="page-subtitle">Flights and transfers. Tap a card for the rest.</p>
      </div>

      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
      {trips === null && !error && <div className="skeleton" style={{ height: 120 }} />}
      {trips?.length === 0 && <p>No trips filed yet.</p>}

      {trips?.map((trip) => {
        const open = openIds.includes(trip.id);
        const needed = neededCount(trip);
        const flights = trip.legs.filter((leg) => leg.kind === 'flight');
        const transfers = trip.legs.filter((leg) => leg.kind === 'transfer');
        const returnNote = trip.notes?.includes(RETURN_RIDES_NOTE) ? RETURN_RIDES_NOTE : null;
        const detailNotes = notesBody(trip.notes);
        return (
          <section key={trip.id} className="card" style={{ padding: 0 }}>
            <button
              type="button"
              className="trip-details__toggle"
              aria-expanded={open}
              onClick={() =>
                setOpenIds((ids) => (ids.includes(trip.id) ? ids.filter((id) => id !== trip.id) : [...ids, trip.id]))
              }
            >
              <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                <span>
                  <strong style={{ display: 'block', fontSize: 18 }}>{trip.title}</strong>
                  {trip.travelers && (
                    <span style={{ display: 'block', color: 'var(--color-ink-muted)', marginTop: 4 }}>{trip.travelers}</span>
                  )}
                  {returnNote && (
                    <span style={{ display: 'block', marginTop: 6 }}>{returnNote}</span>
                  )}
                </span>
                <span aria-hidden="true">{open ? '▾' : '▸'}</span>
              </span>
              {needed > 0 && (
                <span className="badge badge-closed" style={{ marginTop: 8 }}>
                  NEEDED · {needed} {needed === 1 ? 'transfer' : 'transfers'}
                </span>
              )}
            </button>
            {open && (
              <div className="trip-details__body">
                {detailNotes && <TripNotes text={detailNotes} />}
                <h2 className="trip-details__section">Flights</h2>
                {flights.length === 0 && <p>No flights filed.</p>}
                {flights.map((leg) => (
                  <LegCard key={leg.id} leg={leg} unlocked={unlocked} onSaved={load} />
                ))}
                <h2 className="trip-details__section">Transfers</h2>
                {transfers.length === 0 && <p>No transfers filed.</p>}
                {transfers.map((leg) => (
                  <LegCard key={leg.id} leg={leg} unlocked={unlocked} onSaved={load} />
                ))}
                {unlocked && <AddTransfer slug={trip.slug} onSaved={load} />}
              </div>
            )}
          </section>
        );
      })}

      {trips !== null && !unlocked && <WriteUnlock onUnlocked={() => setUnlocked(true)} />}
      {unlocked && (
        <p style={{ fontSize: 14, color: 'var(--color-ink-muted)' }}>
          Writes unlocked on this device. Open a trip and use Edit or Add transfer.
        </p>
      )}
    </div>
  );
}
